import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, RevealPasswordModal } from '../components/common';
import { ROUTES } from '../constants';
import { marketerService, ApiError } from '../services';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

// Partita Iva = exactly 11 numeric digits (Italian VAT format).
const PIVA_PATTERN = /^\d{11}$/;
const PIVA_FORMAT_ERROR = 'Partita Iva must be exactly 11 digits.';

// Form uses merchant-style field names so the validate response (which uses
// the same names) can be applied without per-field mapping. The marketer PUT
// translates billingTown/billingLegalCompanyName to billingCity/billingBusinessName
// at the service layer.
const billingPayloadFromMarketer = m => ({
  billingPartitaIva: (m?.partitaIva ?? m?.vat ?? '').toString(),
  billingLegalCompanyName: (m?.billingBusinessName ?? '').toString(),
  billingAddress: (
    m?.partitaIvaAddress ??
    m?.billingAddress ??
    ''
  ).toString(),
  billingTown: (m?.partitaIvaCity ?? m?.billingCity ?? '').toString(),
  billingProvince: (
    m?.partitaIvaProvince ??
    m?.billingProvince ??
    ''
  ).toString(),
  billingZipCode: (
    m?.partitaIvaZipCode ??
    m?.billingZipCode ??
    ''
  ).toString(),
  billingSdiCode: (m?.billingSdiCode ?? '').toString(),
  billingPecEmail: (m?.billingPecEmail ?? m?.billingPec ?? '').toString(),
});

const MarketerEditPartitaIvaPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const marketerFromState = location.state?.marketer;

  const [marketer, setMarketer] = useState(marketerFromState || null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validateError, setValidateError] = useState(null);
  const [pendingAutoFill, setPendingAutoFill] = useState(null);
  const [validatedMeta, setValidatedMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [stripeSync, setStripeSync] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const initialFetchDoneRef = useRef(false);
  const justVerifiedRef = useRef(false);

  const detailHref = id
    ? ROUTES.generatePath(ROUTES.MARKETERS_DETAIL_LIST, { id })
    : ROUTES.MARKETERS;

  const goBackToDetail = useCallback(() => {
    if (!id) {
      navigate(ROUTES.MARKETERS);
      return;
    }
    navigate(detailHref, { state: marketer ? { marketer } : undefined });
  }, [detailHref, id, marketer, navigate]);

  const loadMarketer = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await marketerService.getFormattedMarketerDetails(id);
      const data = response?.data;
      if (!data) {
        setError('Failed to load creator details.');
        return;
      }
      setMarketer(data);
      const isRevealed = data.revealed === true;
      setRevealed(isRevealed);
      if (isRevealed) {
        setForm(billingPayloadFromMarketer(data));
        // Carry forward the previously-validated raw response from BE so the
        // checked flag stays consistent across edits.
        setValidatedMeta({
          billingPartitaIvaChecked: data.billingPartitaIvaChecked === true,
          billingPartitaIvaRawResponse: data.billingPartitaIvaRawResponse || {},
        });
      } else {
        setForm(billingPayloadFromMarketer({})); // empty form behind modal
        setRevealModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to load creator:', err);
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to load creator. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    loadMarketer();
  }, [loadMarketer]);

  const handleChange = field => e => {
    const nextValue = e.target.value;
    setForm(prev => ({ ...prev, [field]: nextValue }));
    setFieldErrors(prev => {
      if (!prev?.[field]) return prev;
      const trimmed = String(nextValue ?? '').trim();
      if (!trimmed) return prev;
      const { [field]: _ignored, ...rest } = prev;
      return rest;
    });
    if (stripeSync) setStripeSync(null);
  };

  const validate = currentForm => {
    if (!currentForm) return {};
    // Only the 5 required-by-BE fields are user-visible; validate just those.
    const requiredFields = [
      'billingPartitaIva',
      'billingAddress',
      'billingProvince',
      'billingTown',
      'billingZipCode',
    ];
    const next = {};
    for (const key of requiredFields) {
      const value = String(currentForm[key] ?? '').trim();
      if (!value) next[key] = 'This field is required.';
    }
    // Format check overrides the generic "required" error for PIVA.
    const piva = String(currentForm.billingPartitaIva ?? '').trim();
    if (piva && !PIVA_PATTERN.test(piva)) {
      next.billingPartitaIva = PIVA_FORMAT_ERROR;
    }
    return next;
  };

  const otherBillingFieldsFilled = () => {
    if (!form) return false;
    const others = [
      'billingLegalCompanyName',
      'billingAddress',
      'billingTown',
      'billingProvince',
      'billingZipCode',
      'billingSdiCode',
      'billingPecEmail',
    ];
    return others.some(k => String(form[k] ?? '').trim() !== '');
  };

  const applyAutoFill = data => {
    setForm(prev => ({
      ...prev,
      billingPartitaIva: data.billingPartitaIva ?? prev.billingPartitaIva,
      billingLegalCompanyName:
        data.billingLegalCompanyName ?? prev.billingLegalCompanyName,
      billingAddress: data.billingAddress ?? prev.billingAddress,
      billingTown: data.billingTown ?? prev.billingTown,
      billingProvince: data.billingProvince ?? prev.billingProvince,
      billingZipCode: data.billingZipCode ?? prev.billingZipCode,
      billingSdiCode: data.billingSdiCode ?? prev.billingSdiCode,
      billingPecEmail: data.billingPecEmail ?? prev.billingPecEmail,
    }));
    setValidatedMeta({
      billingPartitaIvaChecked: data.billingPartitaIvaChecked ?? false,
      billingPartitaIvaRawResponse: data.billingPartitaIvaRawResponse ?? {},
    });
    setFieldErrors({});
  };

  const handleValidate = async () => {
    if (!id || !form) return;
    const partitaIva = String(form.billingPartitaIva ?? '').trim();
    if (!partitaIva) {
      setValidateError('Please enter a Partita Iva before validating.');
      return;
    }
    if (!PIVA_PATTERN.test(partitaIva)) {
      setValidateError(PIVA_FORMAT_ERROR);
      return;
    }
    setValidating(true);
    setValidateError(null);
    setPendingAutoFill(null);
    try {
      const response = await marketerService.validatePartitaIva(id, partitaIva);
      if (response?.data) {
        // Stash checked + rawResponse the moment validate succeeds — these
        // describe the Partita Iva itself, not the address. They must survive
        // even if admin picks Keep Current (which only declines the address
        // autofill). Editing the Partita Iva input clears this stash.
        setValidatedMeta({
          billingPartitaIvaChecked: response.data.billingPartitaIvaChecked ?? true,
          billingPartitaIvaRawResponse:
            response.data.billingPartitaIvaRawResponse ?? {},
        });
        if (otherBillingFieldsFilled()) {
          setPendingAutoFill(response.data);
        } else {
          applyAutoFill(response.data);
        }
      }
    } catch (err) {
      console.error('Failed to validate Partita Iva:', err);
      const code = err instanceof ApiError ? err.code : null;
      if (code === 'InvalidRequest') {
        setValidateError('No company matches this Partita Iva.');
      } else {
        setValidateError(
          err instanceof ApiError
            ? err.message
            : 'Failed to validate Partita Iva. Please try again.'
        );
      }
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!id || !form) return;
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setError('Please fill in all required fields.');
      return;
    }
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!id || !form) return;
    setShowConfirm(false);
    setSaving(true);
    setError(null);
    setStripeSync(null);
    try {
      const response = await marketerService.updatePartitaIva(id, {
        billingPartitaIva: form.billingPartitaIva,
        billingLegalCompanyName: form.billingLegalCompanyName,
        billingAddress: form.billingAddress,
        billingTown: form.billingTown,
        billingProvince: form.billingProvince,
        billingZipCode: form.billingZipCode,
        billingSdiCode: form.billingSdiCode,
        billingPecEmail: form.billingPecEmail,
        billingPartitaIvaChecked:
          validatedMeta?.billingPartitaIvaChecked ?? false,
        billingPartitaIvaRawResponse:
          validatedMeta?.billingPartitaIvaRawResponse ?? {},
      });
      // BE returns stripeCustomerSyncStatus + stripeCustomerSyncMessage.
      // Use the message verbatim per release notes section 4.1 step 7.
      const status = response?.data?.stripeCustomerSyncStatus || 'updated';
      const message =
        response?.data?.stripeCustomerSyncMessage ||
        'Partita Iva details saved.';
      setStripeSync({ status, message });
      // Mirror saved values back into the marketer cache so the detail page
      // (when user clicks Back to Details) reflects the new values.
      setMarketer(prev =>
        prev
          ? {
              ...prev,
              partitaIva: String(form.billingPartitaIva).trim(),
              partitaIvaAddress: String(form.billingAddress).trim(),
              partitaIvaProvince: String(form.billingProvince).trim(),
              partitaIvaCity: String(form.billingTown).trim(),
              partitaIvaZipCode: String(form.billingZipCode).trim(),
              billingAddress: String(form.billingAddress).trim(),
              billingProvince: String(form.billingProvince).trim(),
              billingCity: String(form.billingTown).trim(),
              billingZipCode: String(form.billingZipCode).trim(),
              billingBusinessName: String(form.billingLegalCompanyName).trim(),
              billingPecEmail: String(form.billingPecEmail).trim(),
              billingPec: String(form.billingPecEmail).trim(),
              billingSdiCode: String(form.billingSdiCode).trim(),
            }
          : prev
      );
    } catch (err) {
      console.error('Failed to update Partita Iva:', err);
      const code = err instanceof ApiError ? err.code : null;
      if (code === 'FeatureUnauthorized') {
        setError(
          'Your admin session has expired or lacks permission. Please re-verify and try again.'
        );
      } else if (code === 'FeatureInvalidPayload') {
        setError('Required fields are missing or invalid. Please review the values.');
      } else if (code === 'InvalidRequest') {
        setError('No company matches this Partita Iva.');
      } else if (code === 'DatabaseNotFound') {
        setError('Creator not found.');
      } else if (code === 'StripeError') {
        setError(
          err.message ||
            'Stripe customer sync failed. The DB was not updated. Please try again.'
        );
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : 'Failed to save Partita Iva. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const marketerLabel =
    marketer?.fullName ||
    [marketer?.firstName, marketer?.lastName].filter(Boolean).join(' ') ||
    'Creator';

  if (loading && !marketer) {
    return (
      <div className='flex items-center justify-center min-h-[40vh]'>
        <div className='flex items-center space-x-2'>
          <svg
            className='animate-spin h-8 w-8 text-blue-600'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            />
          </svg>
          <span className='text-gray-600'>Loading…</span>
        </div>
      </div>
    );
  }

  const hasFieldError = key => !!fieldErrors?.[key];
  const inputClassFor = key =>
    `${inputClass} ${
      hasFieldError(key)
        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
        : ''
    }`;

  const editable = revealed && !saving;

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Edit Partita Iva'
        subtitle="Edit the creator's Partita Iva and make sure the data is correct"
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Creators', href: ROUTES.MARKETERS },
          { name: marketerLabel, href: detailHref, state: { marketer } },
          { name: 'Edit Partita Iva' },
        ]}
      />

      {stripeSync && (
        <div className='bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-amber-800'>{stripeSync.message}</p>
          <button
            type='button'
            onClick={goBackToDetail}
            className='px-3 py-1.5 border border-amber-300 rounded-md text-xs font-medium text-amber-800 bg-white hover:bg-amber-50 whitespace-nowrap'
          >
            Back to Details
          </button>
        </div>
      )}

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-sm text-red-700'>{error}</p>
        </div>
      )}

      {pendingAutoFill && (
        <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-blue-800'>
            Validation successful. Replace the current billing data with the
            suggested values?
          </p>
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={() => setPendingAutoFill(null)}
              className='px-3 py-1.5 border border-blue-300 rounded-md text-xs font-medium text-blue-700 bg-white hover:bg-blue-50'
            >
              Keep Current
            </button>
            <button
              type='button'
              onClick={() => {
                applyAutoFill(pendingAutoFill);
                setPendingAutoFill(null);
              }}
              className='px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700'
            >
              Replace
            </button>
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='bg-white rounded-lg shadow-sm border border-gray-200'
      >
        <div className='px-4 md:px-6 py-5 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>
            {marketerLabel}
          </h3>
          <p className='text-xs text-gray-500 mt-0.5 break-all'>
            Creator ID: {marketer?.id || '-'}
          </p>
        </div>

        <div className='p-6 space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5'>
            <div>
              <label
                htmlFor='billingPartitaIva'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Partita Iva <span className='text-red-600'>*</span>
              </label>
              <div className='flex gap-2'>
                <input
                  id='billingPartitaIva'
                  type='text'
                  value={form?.billingPartitaIva ?? ''}
                  onChange={e => {
                    handleChange('billingPartitaIva')(e);
                    setValidateError(null);
                    setPendingAutoFill(null);
                    setValidatedMeta(null);
                  }}
                  disabled={!editable}
                  className={`flex-1 ${inputClassFor('billingPartitaIva')}`}
                  autoComplete='off'
                  aria-invalid={hasFieldError('billingPartitaIva')}
                />
                <button
                  type='button'
                  onClick={handleValidate}
                  disabled={!editable || validating}
                  className='px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'
                >
                  {validating ? 'Validating…' : 'Validate'}
                </button>
              </div>
              {hasFieldError('billingPartitaIva') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.billingPartitaIva}
                </p>
              )}
              {validateError && (
                <p className='mt-1 text-xs text-red-600'>{validateError}</p>
              )}
            </div>
            <div>
              <label
                htmlFor='billingAddress'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Address <span className='text-red-600'>*</span>
              </label>
              <input
                id='billingAddress'
                type='text'
                value={form?.billingAddress ?? ''}
                onChange={handleChange('billingAddress')}
                disabled={!editable}
                className={inputClassFor('billingAddress')}
                autoComplete='street-address'
                aria-invalid={hasFieldError('billingAddress')}
              />
              {hasFieldError('billingAddress') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.billingAddress}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor='billingProvince'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                Province <span className='text-red-600'>*</span>
              </label>
              <input
                id='billingProvince'
                type='text'
                value={form?.billingProvince ?? ''}
                onChange={handleChange('billingProvince')}
                disabled={!editable}
                className={inputClassFor('billingProvince')}
                autoComplete='address-level1'
                aria-invalid={hasFieldError('billingProvince')}
              />
              {hasFieldError('billingProvince') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.billingProvince}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor='billingTown'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                City <span className='text-red-600'>*</span>
              </label>
              <input
                id='billingTown'
                type='text'
                value={form?.billingTown ?? ''}
                onChange={handleChange('billingTown')}
                disabled={!editable}
                className={inputClassFor('billingTown')}
                autoComplete='address-level2'
                aria-invalid={hasFieldError('billingTown')}
              />
              {hasFieldError('billingTown') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.billingTown}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor='billingZipCode'
                className='block text-sm font-medium text-gray-900 mb-1'
              >
                ZIP Code <span className='text-red-600'>*</span>
              </label>
              <input
                id='billingZipCode'
                type='text'
                value={form?.billingZipCode ?? ''}
                onChange={handleChange('billingZipCode')}
                disabled={!editable}
                className={inputClassFor('billingZipCode')}
                autoComplete='postal-code'
                aria-invalid={hasFieldError('billingZipCode')}
              />
              {hasFieldError('billingZipCode') && (
                <p className='mt-1 text-xs text-red-600'>
                  {fieldErrors.billingZipCode}
                </p>
              )}
            </div>
          </div>

          <div className='flex flex-wrap items-center justify-end gap-3 pt-2'>
            <button
              type='button'
              onClick={goBackToDetail}
              disabled={saving}
              className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={!editable}
              className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>

      {showConfirm && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-md p-6'>
            <h2 className='text-base font-semibold text-gray-900 mb-3'>
              Confirm Partita Iva Update
            </h2>
            <p className='text-sm text-gray-600 mb-6'>
              Please confirm the update to the creator's partita iva details.
              Under GDPR, you are responsible for ensuring this data is accurate
              and up to date. This change will be logged with your user ID and a
              timestamp.
            </p>
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => setShowConfirm(false)}
                className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleConfirmSave}
                className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <RevealPasswordModal
        isOpen={revealModalOpen}
        onClose={() => {
          setRevealModalOpen(false);
          if (justVerifiedRef.current) {
            justVerifiedRef.current = false;
            return;
          }
          if (!revealed) goBackToDetail();
        }}
        onSuccess={async () => {
          justVerifiedRef.current = true;
          await loadMarketer();
        }}
      />
    </div>
  );
};

export default MarketerEditPartitaIvaPage;
